import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Check, StickyNote } from "lucide-react";

export function JatakSnippets() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/jatak/templates"); setItems(data); }
    catch { /* stille */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!name.trim()) { toast.error("Giv teksten et navn"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/jatak/templates", { name, text });
      setItems((xs) => [...xs, data].sort((a, b) => a.name.localeCompare(b.name)));
      setName(""); setText(""); setShowForm(false);
      toast.success("Tekst gemt");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke gemme"); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    try { await api.delete(`/jatak/templates/${it.id}`); setItems((xs) => xs.filter((x) => x.id !== it.id)); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const copy = async (it) => {
    try {
      await navigator.clipboard.writeText(it.text || "");
      setCopied(it.id); setTimeout(() => setCopied(null), 1500);
      toast.success("Kopieret");
    } catch { toast.error("Kunne ikke kopiere"); }
  };

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="jatak-snippets">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <StickyNote className="w-4 h-4 text-amber-500" />
          Mine tekster · gem faste tekster du bruger til opslag
        </div>
        <Button variant="outline" onClick={() => setShowForm((v) => !v)} className="h-8 rounded-lg gap-1.5 text-xs" data-testid="snippet-new">
          <Plus className="w-3.5 h-3.5" />Ny tekst
        </Button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-3 mb-4 space-y-2" data-testid="snippet-form">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Navn, fx “Standard Ja tak”" data-testid="snippet-name" />
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder={"Skriv teksten her…\n\nJa tak-pris gælder kun i aften kl. 20:00 🛒"} data-testid="snippet-text" />
          <div className="flex justify-end">
            <Button onClick={add} disabled={saving} className="h-8 rounded-lg gap-1.5 text-xs" data-testid="snippet-save">
              <Check className="w-3.5 h-3.5" />Gem tekst
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-1" data-testid="snippet-empty">Ingen gemte tekster endnu — tryk “Ny tekst”.</p>
      ) : (
        <div className="space-y-2" data-testid="snippet-list">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-3" data-testid="snippet-item">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-slate-700">{it.name}</p>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => copy(it)} data-testid="snippet-copy"
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium border border-[#e2e8f0] bg-white text-slate-600 hover:text-emerald-700 hover:border-emerald-300 transition-colors">
                    {copied === it.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}Kopiér
                  </button>
                  <button onClick={() => remove(it)} data-testid="snippet-delete"
                    className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {it.text && <p className="text-sm text-slate-500 whitespace-pre-wrap mt-1">{it.text}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
